import { AllModules, ColDef } from "@ag-grid-enterprise/all-modules";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { AgGridReact } from "@ag-grid-community/react";

import { Alert } from "@blueprintjs/core";

import { GET_ALARM_POLICIES } from "graphql/queries";
import { useAlarmPolicies } from "hooks/usePolicy";
import { usePolicyMutations } from "hooks/usePolicyMutations";
import { useTemplateGridOptions } from "hooks/useTemplateGridOptions";
import { AlarmPolicy } from "types";
import { SelectedCompanyContext } from "utils/context";
import { PolicyToaster } from "utils/toasters";
import { policyKey } from "utils/utils";
import { useSearchParams } from "react-router-dom";

type Props = {
    setPolicy: (p: AlarmPolicy) => void;
    toggle: (isEdit: boolean, policy: AlarmPolicy) => void;
};

export default function PolicyGrid({ setPolicy, toggle }: Props) {
    const [searchParams, setSearchParams] = useSearchParams();
    const getSearchParam = () => {
        return searchParams.get(policyKey);
    };
    const [gridOptions, gridApi] = useTemplateGridOptions(columnDefs, undefined, getSearchParam, true);
    const [company] = useContext(SelectedCompanyContext);
    const [policies, loading, error] = useAlarmPolicies(company?.id);
    //todo: something isn't right with these types.
    const [policyToDelete, setPolicyToDelete] = useState<AlarmPolicy | undefined>();
    const [deleteAlertOpen, setDeleteAlertOpen] = useState(false);
    const {
        deleteMutation: [deleteMutationFunction]
    } = usePolicyMutations();

    const toggleAlert = useCallback(() => {
        setDeleteAlertOpen(!deleteAlertOpen);
    }, [deleteAlertOpen]);

    useEffect(() => {
        if (gridApi && gridOptions) {
            gridOptions.onSelectionChanged = (e) => {
                const selectedNodes = e.api.getSelectedNodes();
                if (selectedNodes && selectedNodes[0]) {
                    searchParams.set(policyKey, btoa(selectedNodes[0].id));
                    setSearchParams(searchParams);
                    setPolicy(selectedNodes[0].data);
                }
            };
        }
    }, [gridApi, gridOptions, searchParams, setPolicy, setSearchParams]);

    const handleDeleteConfirm = useCallback(() => {
        toggleAlert();

        if (!policyToDelete || !company) {
            return;
        }
        deleteMutationFunction({
            variables: {
                policyRelId: policyToDelete.id!
            },
            refetchQueries: [
                {
                    query: GET_ALARM_POLICIES,
                    variables: { companyId: company.id }
                }
            ]
        })
            .then(() => {
                PolicyToaster.show({
                    message: `Successfully Deleted ${policyToDelete.alarmType}.`,
                    intent: "warning"
                });
            })
            .catch(() => {
                PolicyToaster.show({
                    message: `Failed to Delete ${policyToDelete.alarmType}.`,
                    intent: "danger"
                });
            })
            .finally(() => {
                setPolicyToDelete(undefined);
            });
    }, [company, deleteMutationFunction, policyToDelete, toggleAlert]);

    useEffect(() => {
        if (gridApi) {
            if (loading) {
                gridApi.showLoadingOverlay();
            }

            if (!loading && policies) {
                gridApi.hideOverlay();
                gridApi.setRowData(policies);
            }
            //todo: figure out how to show error overlay
            // if (!error) {
            //     gridOptions.overlayNoRowsTemplate =
            //         '<span class="messageStyles">Problem with service link. Please try again later</span>';
            //     gridApi.showNoRowsOverlay();
            // }
        }
    }, [policies, error, gridApi, gridOptions, loading]);
    useEffect(() => {
        if (gridOptions) {
            const getContextMenuItems = (params: any) => {
                params.node.setSelected(true);
                const p = params.node.data as AlarmPolicy;

                return [
                    {
                        name: "Edit",
                        action: () => {
                            toggle(true, p);
                        },
                        icon: "<span class='bp3-icon-standard bp3-icon-edit bp3-intent-primary'></span>"
                    },
                    // {
                    //     name: "Copy Link",
                    //     action: () => {
                    //         navigator.clipboard.writeText(window.location.href);
                    //         CopyToaster.show({
                    //             message: `Copied to Clipboard`,
                    //             intent: "success",
                    //             timeout: 1500
                    //         });
                    //     },
                    //     icon: "<span class='bp3-icon-standard bp3-icon-link bp3-intent-primary'></span>"
                    // },
                    "separator",

                    {
                        name: "Delete",
                        action: () => {
                            setPolicyToDelete(p);
                            toggleAlert();
                        },
                        icon: "<span class='bp3-icon-standard bp3-icon-trash bp3-intent-danger'></span>"
                    }
                    // {
                    //     name: "Delete",
                    //     action: () => {
                    //         const node: AvailableTemplate = params.node.data;
                    //         setSelectedTemplate(node);
                    //         setConfirmOpen(true);
                    //     },
                    //     icon: "<span class='ag-icon ag-icon-cross'></span>"
                    // }
                ];
            };
            gridOptions.getContextMenuItems = getContextMenuItems;
        }
    }, [gridOptions, setPolicy, toggle, toggleAlert]);

    return (
        <>
            <Alert
                cancelButtonText="Cancel"
                confirmButtonText="Confirm"
                icon="trash"
                intent="danger"
                isOpen={deleteAlertOpen}
                onCancel={toggleAlert}
                onConfirm={handleDeleteConfirm}
            >
                <p>
                    Are you sure you want to delete this policy?
                    <b>This will delete the policy and all actions associated with it!</b>
                </p>
            </Alert>
            <AgGridReact modules={AllModules} gridOptions={gridOptions} />
        </>
    );
}

const columnDefs: ColDef[] = [
    {
        colId: "alarmType",
        headerName: "Alarm Type",
        field: "alarmType",
        cellClass: "no-border"
    },
    {
        colId: "type",
        headerName: "Action Type",
        field: "type",
        sortable: true,
        filter: true
    },
    {
        colId: "policies",
        headerName: "Actions",
        field: "policies",
        valueFormatter: (p) => p.value.map((d: any) => d.name).toString(),
        tooltipValueGetter: (p) => {
            return p.valueFormatted;
        },
        cellClass: "no-border",
        flex: 1
    }
];
